import { isModalOpen } from '../modals.js';
/**
 * Vault UI Module - Orchestrator
 * Map library with folder navigation, search and map actions.
 *
 * This module provides the public API and coordinates between sub-modules.
 */

import * as State from './state.js';
import * as List from './list.js';
import * as Search from './search.js';
import * as Actions from './actions.js';
import { initPicker, openPicker } from './picker.js';
import { on, Events } from "../../../../engine/core/events.js";
import { engine } from "../../../../engine/index.js";
import { initAccountDeletion } from "../account-deletion.js";
import { createStorageSummary } from "../storage-usage.js";
import { getStorageInfo, invalidateStorageInfo } from "../../../../engine/services/storage-service.js";
import { t } from '../../../../shared/i18n/index.js';


// ============================================
// Module State
// ============================================

let _shareRevokedUnsub = null;
let _quotaUnsub = null;
let _indexUnsub = null;

// ============================================
// Initialization
// ============================================

/**
 * Initialize the vault UI
 * @param {Object} deps - Dependencies
 * @param {Function} deps.onDocOpen - Callback when doc is opened
 * @param {Function} deps.onDocCreate - Callback when doc is created
 * @param {Function} deps.onDocDelete - Callback when doc is deleted
 * @param {Function} deps.onShareOpen - Callback when share is opened
 * @param {Function} deps.onClose - Callback when vault is closed
 * @param {Object} deps.storage - VaultStorage backend
 * @param {Function} deps.getMasterKey - Function to get master key
 * @param {Function} deps.onLogout - Callback for logout
 */
export function init(deps = {}) {
  // Clean up any existing listeners first
  destroy();

  // Create new AbortController for this init cycle
  const controller = new AbortController();
  State.setAbortController(controller);

  // Set callbacks
  State.setCallbacks(deps);

  // Set storage backend
  if (deps.storage) {
    State.setVaultStorage(deps.storage);
  }

  const VaultStorage = State.getVaultStorage();

  if (!VaultStorage) {
    console.warn(
      "[VaultUI] No storage backend provided. Vault features will be limited.",
    );
  }

  // Initialize account deletion module
  if (VaultStorage && deps.getMasterKey) {
    initAccountDeletion({
      VaultStorage: VaultStorage,
      getMasterKey: deps.getMasterKey,
      onLogout: deps.onLogout,
    });
  }

  // Initialize sub-modules with dependencies
  Search.initSearch({
    escapeHtml: List.escapeHtml,
    renderMapItem: List.renderMapItem,
    getFolderPathString: List.getFolderPathString,
  });

  Actions.initActions({
    render: List.render,
    close: close,
    getFolderPathString: List.getFolderPathString,
  });

  initPicker({
    escapeHtml: List.escapeHtml,
  });

  // Setup event listeners
  setupEventListeners(controller.signal);
  State.setListenersAttached(true);

  // Listen for share revoked events to re-render vault UI
  _shareRevokedUnsub = on(Events.SHARE_REVOKED, handleShareRevoked);
  // The index changed from sync (signing in, another device): the open
  // list shows it. Not while the user is in the list (a menu open, focus
  // on a row): the next render (any action, or reopening) shows it.
  _indexUnsub = on(Events.VAULT_INDEX_UPDATED, () => {
    const content = document.getElementById("vault-panel-content");
    if (!State.isOpen() || !content) return;
    if (content.contains(document.activeElement) || content.querySelector("details[open]")) return;
    List.render();
  });
  _quotaUnsub = on(Events.QUOTA_CHANGED, ({ info }) => {
    const target = document.getElementById('vault-panel-storage');
    if (info && target && State.isOpen()) target.replaceChildren(createStorageSummary(info));
  });

  console.log(
    "[VaultUI] Initialized with storage:",
    VaultStorage ? "yes" : "no",
  );
}

/**
 * Clean up vault UI and remove all event listeners
 */
export function destroy() {
  _quotaUnsub?.();
  _quotaUnsub = null;
  _indexUnsub?.();
  _indexUnsub = null;
  if (_shareRevokedUnsub) {
    _shareRevokedUnsub();
    _shareRevokedUnsub = null;
  }
  const controller = State.getAbortController();
  if (controller) {
    controller.abort();
    State.setAbortController(null);
  }
  State.setListenersAttached(false);
}

/**
 * Setup event listeners for vault UI
 * @param {AbortSignal} signal - AbortController signal for cleanup
 */
function setupEventListeners(signal) {
  // Toggle button
  const toggleBtn = document.getElementById("vault-btn");
  console.log("[VaultUI] setupEventListeners, toggleBtn:", toggleBtn);
  if (toggleBtn) {
    toggleBtn.addEventListener("click", (e) => {
      console.log("[VaultUI] Toggle button clicked!");
      e.stopPropagation();
      toggle();
    }, { signal });
  }

  // Close button
  const closeBtn = document.getElementById("vault-panel-close");
  if (closeBtn) {
    closeBtn.addEventListener("click", () => close(), { signal });
  }

  // Overlay click
  const overlay = document.getElementById("vault-overlay");
  if (overlay) {
    overlay.addEventListener("click", () => close(), { signal });
  }

  // Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && State.isOpen() && !isModalOpen()) {
      close();
    }
  }, { signal });

  document.getElementById('vault-panel')?.addEventListener('keydown', event => {
    if (isModalOpen()) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key === 'Tab') {
      const targets = [...event.currentTarget.querySelectorAll('button:not([disabled]), input:not([hidden]), summary, [tabindex="0"]')].filter(el => el.getClientRects().length);
      const first = targets[0], last = targets.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    event.stopPropagation();
  }, { signal });

  // Search input
  const searchInput = document.getElementById("vault-search-input");
  if (searchInput) {
    searchInput.addEventListener("input", (e) => {
      State.setSearchQuery(e.target.value.trim());
      List.render();
    }, { signal });
  }

  // Clear search
  const searchClear = document.getElementById("vault-search-clear");
  if (searchClear) {
    searchClear.addEventListener("click", () => {
      State.setSearchQuery("");
      const input = document.getElementById("vault-search-input");
      if (input) input.value = "";
      List.render();
    }, { signal });
  }


}

/**
 * Handle share revoked event
 */
async function handleShareRevoked(data) {
  // Re-render vault if open
  if (State.isOpen()) {
    List.render();
  }
}

// ============================================
// Public API
// ============================================

/**
 * Open the vault panel
 */
export async function open() {
  const VaultStorage = State.getVaultStorage();


  if (!VaultStorage) {
    console.error("[VaultUI] No VaultStorage set!");
    return;
  }

  const index = VaultStorage.getIndex();

  if (!index) {
    console.warn("[VaultUI] No index available, cannot open vault panel");
    return;
  }

  invalidateStorageInfo();
  State.setOpen(true);
  document.getElementById("vault-panel")?.classList.add("open");
  document.getElementById("vault-overlay")?.classList.add("open");

  // Keep navigation when reopening, defaulting to the complete library.
  const selected = State.getSelectedFolderId();
  if (![State.CATEGORY_ALL, State.CATEGORY_RECENT, State.CATEGORY_SHARED].includes(selected) && !index.folders.some(f => f.id === selected)) {
    State.setSelectedFolderId(State.CATEGORY_ALL);
  }
  const panel = document.getElementById('vault-panel');
  panel.inert = false;
  panel.style.width = `${State.loadSizes().panelWidth}px`;
  document.getElementById('vault-btn')?.setAttribute('aria-expanded', 'true');
  document.getElementById('vault-search-input')?.focus();

  // Only fetch shares if we're in online mode (onShareOpen callback exists)
  const callbacks = State.getCallbacks();
  if (callbacks.onShareOpen) {
    // Fetch owned shares in background
    Actions.fetchOwnedShares().then(() => {
      // Re-render to show owned shares
      if (State.isOpen()) List.render();
    });

    // Validate shared docs in background (clean up revoked ones)
    Actions.validateSharedDocs().then((hasChanges) => {
      if (hasChanges && State.isOpen()) List.render();
    });
  }

  List.render();

  // Load and display storage quota (async, non-blocking)
  loadQuotaBar();
}

/**
 * Load storage quota info and render the quota bar in the vault footer.
 */
async function loadQuotaBar() {
  const storageEl = document.getElementById("vault-panel-storage");
  if (!storageEl) return;
  try {
    const info = await getStorageInfo({ force: true });
    storageEl.innerHTML = '';
    storageEl.appendChild(createStorageSummary(info));
  } catch {
    // Offline or not authenticated — hide the bar
    storageEl.innerHTML = '';
  }
}

/**
 * Close the vault panel
 */
export function close() {
  State.setOpen(false);
  document.getElementById('vault-panel').inert = true;
  document.getElementById('vault-btn')?.setAttribute('aria-expanded', 'false');
  document.getElementById('vault-btn')?.focus();
  document.getElementById("vault-panel")?.classList.remove("open");
  document.getElementById("vault-overlay")?.classList.remove("open");

  const callbacks = State.getCallbacks();
  if (callbacks.onClose) callbacks.onClose();
}

/**
 * Toggle the vault panel
 */
export function toggle() {
  console.log("[VaultUI] toggle() called, isOpen:", State.isOpen());
  if (State.isOpen()) close();
  else open();
}

/**
 * Update the current doc name display
 */
/** The vault button: shown while signed in, with the open map's name. */
export function updateVaultButton(signedIn) {
  const vaultToggleBtn = document.getElementById("vault-btn");
  if (!vaultToggleBtn) return;
  vaultToggleBtn.classList.toggle("hidden", !signedIn);
  if (!signedIn) return;
  try {
    updateCurrentDocName();
  } catch {
    // The vault is not loaded yet: its name comes when a map opens.
  }
}

export function updateCurrentDocName() {
  const VaultStorage = State.getVaultStorage();

  const el = document.getElementById("doc-title-text");
  if (!el) return;

  const index = VaultStorage.getIndex();
  // A map open by its link for this page only: not in the vault.
  const open = engine.map.id?.();
  const held = open && !index?.docs?.some(d => d.docId === open) ? engine.sharing.link.held(open) : null;
  if (held) {
    el.textContent = held.title || t('common.untitled');
    return;
  }
  const currentDocId = VaultStorage.getCurrentDocId();
  if (index && currentDocId) {
    const docEntry = index.docs?.find(d => d.docId === currentDocId);
    if (docEntry) {
      el.textContent = docEntry.title || "";
      return;
    }
  }
  el.textContent = t('vault.myMaps');
}

// ============================================
// Re-exports for Public API
// ============================================

// From list.js
export { render } from './list.js';
export { escapeHtml, getFolderPath, getFolderPathString } from './list.js';

// From actions.js
export { openPersonalDoc, openDoc } from './actions.js';

// From picker.js
export { openPicker } from './picker.js';

/** Create a map from the header using the same dialog as the vault toolbar. */
export function promptNewDoc() {
  return Actions.promptNewDocInFolder(State.getSelectedFolderId());
}
